// Blog pages — centralized in @denqfabrik/ghost-blog (no more local copies)
import { BlogListPage, BlogPostPage } from '@denqfabrik/ghost-blog/pages';
import { LandingProvider } from '@denqfabrik/landing-ui';
import { SentryErrorBoundary } from '@denqfabrik/sentry/client';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import brandConfig from './brand.config';
import Layout from './components/layout/Layout';
import useDocumentTitle from './hooks/useDocumentTitle';
import { LanguageProvider, useTranslation } from './i18n';
import { DE_LEGACY_REDIRECTS, EN_ALIAS_REDIRECTS } from './i18n/routes';
// Critical: HomePage loaded eagerly (most common entry point)
import HomePage from './pages/HomePage';

// All other pages lazy-loaded — split into separate chunks
const KiAgentenPage = lazy(() => import('./pages/KiAgentenPage'));
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'));
const UseCaseDetailPage = lazy(() => import('./pages/UseCaseDetailPage'));
const SicherheitPage = lazy(() => import('./pages/SicherheitPage'));
const IntegrationenPage = lazy(() => import('./pages/IntegrationenPage'));
const AmazonSpApiPage = lazy(() => import('./pages/AmazonSpApiPage'));
const MiraklPage = lazy(() => import('./pages/MiraklPage'));
const WooCommercePage = lazy(() => import('./pages/WooCommercePage'));
const ShopwarePage = lazy(() => import('./pages/ShopwarePage'));
const UeberUnsPage = lazy(() => import('./pages/UeberUnsPage'));
const KontaktPage = lazy(() => import('./pages/KontaktPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const DatenschutzPage = lazy(() => import('./pages/DatenschutzPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const BookingConfirmedPage = lazy(() => import('./pages/BookingConfirmedPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ImprintPage = lazy(() => import('./pages/ImprintPage'));

const blogConfig = {
  baseUrl: 'https://denqfabrik.com',
  siteName: 'Denqfabrik',
  langPrefix: 'both' as const,
  heroTitle: { de: 'Aktuelle Einblicke', en: 'Latest Insights' },
  heroSubtitle: {
    de: 'Einblicke in E-Commerce-Automatisierung, Systemintegrationen und KI-Agenten',
    en: 'Insights on e-commerce automation, system integrations, and AI agents',
  },
  proseClassName: 'prose-blog',
};

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <LanguageProvider>
      <Layout>{children}</Layout>
    </LanguageProvider>
  );
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

// The shared BlogListPage sets no <head>; this adds the translated one (same idea as
// AletiqBlogListPage in apps/aletiq-landing). Must render inside PageShell (useTranslation).
function BlogListWithHead() {
  const { t } = useTranslation();
  useDocumentTitle(t.blog.meta);
  return <BlogListPage config={blogConfig} />;
}

function App() {
  return (
    <LandingProvider config={brandConfig}>
      <SentryErrorBoundary fallback={<p>Something went wrong</p>}>
        <Routes>
          {/* Root → /de */}
          <Route path="/" element={<Navigate to="/de" replace />} />

          {/* German routes (/de prefix) */}
          <Route
            path="/de"
            element={
              <PageShell>
                <HomePage />
              </PageShell>
            }
          />
          <Route
            path="/de/ki-agenten"
            element={
              <PageShell>
                <LazyPage>
                  <KiAgentenPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/use-cases"
            element={
              <PageShell>
                <LazyPage>
                  <UseCasesPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/use-cases/:slug"
            element={
              <PageShell>
                <LazyPage>
                  <UseCaseDetailPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/sicherheit"
            element={
              <PageShell>
                <LazyPage>
                  <SicherheitPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/integrationen"
            element={
              <PageShell>
                <LazyPage>
                  <IntegrationenPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/integrationen/amazon-sp-api"
            element={
              <PageShell>
                <LazyPage>
                  <AmazonSpApiPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/integrationen/mirakl"
            element={
              <PageShell>
                <LazyPage>
                  <MiraklPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/integrationen/woocommerce"
            element={
              <PageShell>
                <LazyPage>
                  <WooCommercePage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/integrationen/shopware"
            element={
              <PageShell>
                <LazyPage>
                  <ShopwarePage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/pricing"
            element={
              <PageShell>
                <LazyPage>
                  <PricingPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/ueber-uns"
            element={
              <PageShell>
                <LazyPage>
                  <UeberUnsPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/kontakt"
            element={
              <PageShell>
                <LazyPage>
                  <KontaktPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/impressum"
            element={
              <PageShell>
                <LazyPage>
                  <ImpressumPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/datenschutz"
            element={
              <PageShell>
                <LazyPage>
                  <DatenschutzPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/danke"
            element={
              <PageShell>
                <LazyPage>
                  <ThankYouPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/de/blog"
            element={
              <PageShell>
                <BlogListWithHead />
              </PageShell>
            }
          />
          <Route
            path="/de/blog/:slug"
            element={
              <PageShell>
                <BlogPostPage config={blogConfig} />
              </PageShell>
            }
          />

          {/* English routes (/en prefix) */}
          <Route
            path="/en"
            element={
              <PageShell>
                <HomePage />
              </PageShell>
            }
          />
          <Route
            path="/en/ai-agents"
            element={
              <PageShell>
                <LazyPage>
                  <KiAgentenPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/use-cases"
            element={
              <PageShell>
                <LazyPage>
                  <UseCasesPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/use-cases/:slug"
            element={
              <PageShell>
                <LazyPage>
                  <UseCaseDetailPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/security"
            element={
              <PageShell>
                <LazyPage>
                  <SicherheitPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/integrations"
            element={
              <PageShell>
                <LazyPage>
                  <IntegrationenPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/integrations/amazon-sp-api"
            element={
              <PageShell>
                <LazyPage>
                  <AmazonSpApiPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/integrations/mirakl"
            element={
              <PageShell>
                <LazyPage>
                  <MiraklPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/integrations/woocommerce"
            element={
              <PageShell>
                <LazyPage>
                  <WooCommercePage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/integrations/shopware"
            element={
              <PageShell>
                <LazyPage>
                  <ShopwarePage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/pricing"
            element={
              <PageShell>
                <LazyPage>
                  <PricingPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/about-us"
            element={
              <PageShell>
                <LazyPage>
                  <UeberUnsPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/contact"
            element={
              <PageShell>
                <LazyPage>
                  <KontaktPage />
                </LazyPage>
              </PageShell>
            }
          />
          {/* /en/impressum und /en/datenschutz rendern hier NICHT mehr eigene
              Seiten — sie leiteten als Einzige nicht um und erzeugten damit
              denselben englischen Inhalt unter zwei Adressen. Die Weiterleitung
              kommt jetzt aus EN_ALIAS_REDIRECTS, wie bei allen anderen Paaren. */}
          <Route
            path="/en/imprint"
            element={
              <PageShell>
                <LazyPage>
                  <ImprintPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/privacy"
            element={
              <PageShell>
                <LazyPage>
                  <PrivacyPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/thank-you"
            element={
              <PageShell>
                <LazyPage>
                  <ThankYouPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/blog"
            element={
              <PageShell>
                <BlogListWithHead />
              </PageShell>
            }
          />
          <Route
            path="/en/blog/:slug"
            element={
              <PageShell>
                <BlogPostPage config={blogConfig} />
              </PageShell>
            }
          />

          {/* Deutsche Slugs unter /en → englisches Gegenstück.
              AUS DER i18n-KARTE ERZEUGT, nicht aufgezählt: die Hand-Liste hatte
              /en/impressum und /en/datenschutz als Duplikat-Seiten und /en/danke
              gar nicht. Ein neues Paar in ROUTE_MAP bringt seine Weiterleitung
              jetzt automatisch mit. */}
          {EN_ALIAS_REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}

          {/* Unpräfigierte Altpfade → /de. Ebenfalls aus der Karte erzeugt. */}
          {DE_LEGACY_REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          {/* Diese bleiben von Hand: echte Altlasten ohne Eintrag in ROUTE_MAP. */}
          <Route
            path="/amazon-sp-api"
            element={<Navigate to="/de/integrationen/amazon-sp-api" replace />}
          />
          <Route path="/thank-you" element={<Navigate to="/en/thank-you" replace />} />
          <Route path="/systeme-loesungen" element={<Navigate to="/de/integrationen" replace />} />
          <Route path="/solutions" element={<Navigate to="/de/integrationen" replace />} />
          <Route path="/solutions/:slug" element={<Navigate to="/de/integrationen" replace />} />
          <Route path="/services" element={<Navigate to="/de/use-cases" replace />} />
          <Route path="/services/:slug" element={<Navigate to="/de/use-cases" replace />} />
          <Route path="/projekte" element={<Navigate to="/de/use-cases" replace />} />
          <Route path="/projekte/:slug" element={<Navigate to="/de/use-cases" replace />} />
          <Route path="/preise" element={<Navigate to="/de/pricing" replace />} />
          <Route path="/blog" element={<Navigate to="/de/blog" replace />} />
          <Route path="/blog/:slug" element={<Navigate to="/de/blog" replace />} />

          {/* Booking confirmation — language-neutral URL for Zoom Scheduler redirect.
              Rendered WITHOUT PageShell so no /de or /en prefix is required;
              the page detects browser language itself. Used as the single Google
              Ads conversion URL. */}
          <Route
            path="/booking-confirmed"
            element={
              <LazyPage>
                <BookingConfirmedPage />
              </LazyPage>
            }
          />

          {/* 404 catch-all for valid lang prefixes */}
          <Route
            path="/de/*"
            element={
              <PageShell>
                <LazyPage>
                  <NotFoundPage />
                </LazyPage>
              </PageShell>
            }
          />
          <Route
            path="/en/*"
            element={
              <PageShell>
                <LazyPage>
                  <NotFoundPage />
                </LazyPage>
              </PageShell>
            }
          />
          {/* Invalid lang or unknown paths → /de */}
          <Route path="*" element={<Navigate to="/de" replace />} />
        </Routes>
      </SentryErrorBoundary>
    </LandingProvider>
  );
}

export default App;
